import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import './ExploratoryTestRunner.css';

/**
 * 🤖 ExploratoryTestRunner
 * Interface pour les tests exploratoires automatiques via FARA-7B
 * 
 * Workflow :
 * 1. Vérifier la config FARA-7B
 * 2. Dry-run : prévisualiser les scénarios extraits du graphe
 * 3. Lancer l'exécution via FARA-7B
 * 4. Suivre la progression en temps réel
 * 5. Afficher le rapport avec verdicts et insights
 */
const ExploratoryTestRunner = ({ onClose }) => {
  const { t } = useTranslation();

  // États principaux
  const [configStatus, setConfigStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  // États UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('setup'); // setup | running | report | history

  // Options d'exécution
  const [options, setOptions] = useState({
    targetUrl: '',
    nodeNames: '',
    types: '',
    maxScenarios: 10,
    onlyUntested: false,
    includeNavigation: false
  });

  // Vérifier la configuration au montage
  useEffect(() => {
    checkConfig();
    loadHistory();
  }, []);

  // Polling du statut pendant l'exécution
  useEffect(() => {
    let interval;
    if (activeRun && ['initializing', 'extracting', 'executing', 'generating_report'].includes(activeRun.status)) {
      interval = setInterval(() => pollRunStatus(activeRun.runId), 3000);
    }
    return () => clearInterval(interval);
  }, [activeRun?.runId, activeRun?.status]);

  const checkConfig = async () => {
    try {
      const res = await apiClient.get('/api/exploratory-tests/config');
      setConfigStatus(res.data.config);
    } catch (err) {
      setConfigStatus({ valid: false, errors: ['Impossible de vérifier la configuration'] });
    }
  };

  const loadHistory = async () => {
    try {
      const res = await apiClient.get('/api/exploratory-tests/history');
      setRunHistory(res.data.history || []);
    } catch {
      // silently fail
    }
  };

  const handleDryRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const body = buildRequestBody();
      const res = await apiClient.post('/api/exploratory-tests/dry-run', body);
      setPreview(res.data.preview);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la prévisualisation');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const body = buildRequestBody();
      const res = await apiClient.post('/api/exploratory-tests/run', body);

      if (res.data.runId) {
        setActiveRun({ runId: res.data.runId, status: 'initializing', progress: { completed: 0, total: 0 } });
        setActiveTab('running');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du lancement');
    } finally {
      setLoading(false);
    }
  };

  const pollRunStatus = useCallback(async (runId) => {
    try {
      const res = await apiClient.get(`/api/exploratory-tests/run/${runId}`);
      const run = res.data.run;
      setActiveRun(run);

      if (run.status === 'completed' || run.status === 'error' || run.status === 'cancelled') {
        setActiveTab('report');
        loadHistory();
      }
    } catch {
      // silently fail polling
    }
  }, []);

  const handleCancelRun = async () => {
    if (!activeRun?.runId) return;
    try {
      await apiClient.post(`/api/exploratory-tests/run/${activeRun.runId}/cancel`);
      setActiveRun(prev => ({ ...prev, status: 'cancelled' }));
    } catch {
      // silently fail
    }
  };

  const loadReport = async (reportId) => {
    try {
      const res = await apiClient.get(`/api/exploratory-tests/report/${reportId}`);
      setSelectedReport(res.data.report);
      setActiveTab('report');
    } catch {
      setError('Impossible de charger le rapport');
    }
  };

  const buildRequestBody = () => {
    const body = {};
    if (options.targetUrl) body.targetUrl = options.targetUrl;
    if (options.nodeNames) body.nodeNames = options.nodeNames.split(',').map(s => s.trim()).filter(Boolean);
    if (options.types) body.types = options.types.split(',').map(s => s.trim()).filter(Boolean);
    if (options.maxScenarios) body.maxScenarios = parseInt(options.maxScenarios, 10);
    body.onlyUntested = options.onlyUntested;
    body.includeNavigation = options.includeNavigation;
    return body;
  };

  return (
    <div className="exploratory-runner">
      {/* Header */}
      <div className="exploratory-header">
        <div className="exploratory-title">
          <span className="exploratory-icon">🤖</span>
          <div>
            <h2>Tests Exploratoires Automatiques</h2>
            <p className="exploratory-subtitle">Propulsé par FARA-7B (Microsoft Research)</p>
          </div>
        </div>
        {onClose && (
          <button className="exploratory-close" onClick={onClose}>✕</button>
        )}
      </div>

      {/* Config Status */}
      {configStatus && (
        <div className={`config-banner ${configStatus.valid ? 'config-ok' : 'config-error'}`}>
          <span>{configStatus.valid ? '✅' : '⚠️'}</span>
          <div>
            <strong>{configStatus.valid ? 'FARA-7B configuré' : 'Configuration requise'}</strong>
            {configStatus.valid ? (
              <span className="config-details">
                Provider: {configStatus.provider} · Cible: {configStatus.targetUrl} · Max steps: {configStatus.maxSteps}
              </span>
            ) : (
              <span className="config-details">{configStatus.errors?.join(', ')}</span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="exploratory-tabs">
        <button className={`tab ${activeTab === 'setup' ? 'active' : ''}`} onClick={() => setActiveTab('setup')}>
          ⚙️ Configuration
        </button>
        <button className={`tab ${activeTab === 'running' ? 'active' : ''}`} onClick={() => setActiveTab('running')} disabled={!activeRun}>
          🔄 Exécution
        </button>
        <button className={`tab ${activeTab === 'report' ? 'active' : ''}`} onClick={() => setActiveTab('report')}>
          📊 Rapport
        </button>
        <button className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          📋 Historique
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="exploratory-error">
          <span>❌</span> {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Setup Tab */}
      {activeTab === 'setup' && (
        <div className="tab-content">
          <div className="options-form">
            <div className="form-group">
              <label>URL cible de l'application</label>
              <input
                type="url"
                value={options.targetUrl}
                onChange={e => setOptions(prev => ({ ...prev, targetUrl: e.target.value }))}
                placeholder="http://localhost:3000 (par défaut)"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nœuds spécifiques (optionnel)</label>
                <input
                  type="text"
                  value={options.nodeNames}
                  onChange={e => setOptions(prev => ({ ...prev, nodeNames: e.target.value }))}
                  placeholder="Login, Dashboard, API Users..."
                />
                <small>Noms séparés par des virgules. Vide = tous les nœuds.</small>
              </div>

              <div className="form-group">
                <label>Types de nœuds</label>
                <input
                  type="text"
                  value={options.types}
                  onChange={e => setOptions(prev => ({ ...prev, types: e.target.value }))}
                  placeholder="feature, api, page..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Max scénarios</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={options.maxScenarios}
                  onChange={e => setOptions(prev => ({ ...prev, maxScenarios: e.target.value }))}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={options.onlyUntested}
                    onChange={e => setOptions(prev => ({ ...prev, onlyUntested: e.target.checked }))}
                  />
                  Uniquement les tests non exécutés
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={options.includeNavigation}
                    onChange={e => setOptions(prev => ({ ...prev, includeNavigation: e.target.checked }))}
                  />
                  Inclure les parcours de navigation
                </label>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button className="btn-secondary" onClick={handleDryRun} disabled={loading}>
              {loading ? '⏳ Analyse...' : '🔍 Prévisualiser (Dry Run)'}
            </button>
            <button className="btn-primary" onClick={handleStartRun} disabled={loading || !configStatus?.valid}>
              {loading ? '⏳ Démarrage...' : '🚀 Lancer FARA-7B'}
            </button>
          </div>

          {/* Preview Results */}
          {preview && (
            <div className="preview-section">
              <h3>📋 Prévisualisation ({preview.scenarios?.length || 0} scénarios)</h3>
              <div className="preview-meta">
                <span>🔢 {preview.totalEstimatedSteps} étapes estimées</span>
                <span>⏱️ ~{preview.estimatedDuration}</span>
                {preview.navigationPaths > 0 && <span>🧭 {preview.navigationPaths} parcours</span>}
              </div>

              <div className="preview-list">
                {(preview.tasks || []).map((task, i) => (
                  <div key={i} className="preview-item">
                    <div className="preview-item-header">
                      <span className={`priority-badge priority-${task.priority}`}>{task.priority}</span>
                      <strong>{task.scenarioName}</strong>
                      <span className="step-count">{task.maxSteps} steps</span>
                    </div>
                    <p className="preview-prompt">{task.prompt}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Running Tab */}
      {activeTab === 'running' && activeRun && (
        <div className="tab-content">
          <div className="running-section">
            <div className="running-status">
              <div className={`status-indicator status-${activeRun.status}`}>
                {activeRun.status === 'executing' ? '🤖' : activeRun.status === 'completed' ? '✅' : activeRun.status === 'error' ? '❌' : '⏳'}
              </div>
              <div>
                <h3>
                  {activeRun.status === 'initializing' && 'Initialisation...'}
                  {activeRun.status === 'extracting' && 'Extraction des scénarios...'}
                  {activeRun.status === 'executing' && 'FARA-7B en action...'}
                  {activeRun.status === 'generating_report' && 'Génération du rapport...'}
                  {activeRun.status === 'completed' && 'Exécution terminée !'}
                  {activeRun.status === 'error' && 'Erreur !'}
                  {activeRun.status === 'cancelled' && 'Annulé'}
                </h3>
                {activeRun.progress?.current && (
                  <p className="current-scenario">📍 {activeRun.progress.current}</p>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            {activeRun.progress?.total > 0 && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(activeRun.progress.completed / activeRun.progress.total) * 100}%`
                    }}
                  />
                </div>
                <span className="progress-text">
                  {activeRun.progress.completed} / {activeRun.progress.total} scénarios
                </span>
              </div>
            )}

            {/* Summary if completed */}
            {activeRun.summary && (
              <div className="run-summary-cards">
                <div className="summary-card card-total">
                  <div className="card-number">{activeRun.summary.total}</div>
                  <div className="card-label">Total</div>
                </div>
                <div className="summary-card card-passed">
                  <div className="card-number">{activeRun.summary.passed}</div>
                  <div className="card-label">Réussis</div>
                </div>
                <div className="summary-card card-failed">
                  <div className="card-number">{activeRun.summary.failed}</div>
                  <div className="card-label">Échoués</div>
                </div>
                <div className="summary-card card-rate">
                  <div className="card-number">{activeRun.summary.passRate}%</div>
                  <div className="card-label">Taux</div>
                </div>
              </div>
            )}

            {activeRun.status === 'executing' && (
              <button className="btn-danger" onClick={handleCancelRun}>
                ⏹️ Annuler l'exécution
              </button>
            )}

            {activeRun.error && (
              <div className="exploratory-error">
                <span>❌</span> {activeRun.error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report Tab */}
      {activeTab === 'report' && (
        <div className="tab-content">
          {activeRun?.summary ? (
            <div className="report-section">
              <h3>📊 Résultats de l'exécution</h3>

              <div className="run-summary-cards">
                <div className="summary-card card-total">
                  <div className="card-number">{activeRun.summary.total}</div>
                  <div className="card-label">Scénarios</div>
                </div>
                <div className="summary-card card-passed">
                  <div className="card-number">{activeRun.summary.passed}</div>
                  <div className="card-label">Réussis</div>
                </div>
                <div className="summary-card card-failed">
                  <div className="card-number">{activeRun.summary.failed + (activeRun.summary.errors || 0)}</div>
                  <div className="card-label">Échoués</div>
                </div>
                <div className="summary-card card-rate">
                  <div className="card-number">{activeRun.summary.passRate}%</div>
                  <div className="card-label">Taux</div>
                </div>
              </div>

              <div className="report-meta">
                <span>⏱️ Durée: {formatDuration(activeRun.summary.totalDuration)}</span>
                <span>📐 Moy: {activeRun.summary.avgStepsPerScenario} étapes/scénario</span>
              </div>

              <div className="report-gauge">
                <div className="gauge-bar">
                  <div
                    className="gauge-fill"
                    style={{
                      width: `${activeRun.summary.passRate}%`,
                      background: activeRun.summary.passRate >= 80 ? '#22c55e'
                        : activeRun.summary.passRate >= 50 ? '#f59e0b' : '#ef4444'
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <span className="empty-icon">📊</span>
              <p>Aucun rapport disponible. Lancez une exécution pour voir les résultats.</p>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="tab-content">
          <h3>📋 Historique des exécutions</h3>
          {runHistory.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📋</span>
              <p>Aucune exécution passée.</p>
            </div>
          ) : (
            <div className="history-list">
              {runHistory.map((run, i) => (
                <div key={i} className={`history-item status-${run.status}`}>
                  <div className="history-header">
                    <span className={`status-badge ${run.status}`}>
                      {run.status === 'completed' ? '✅' : run.status === 'error' ? '❌' : '⏳'}
                      {run.status}
                    </span>
                    <span className="history-date">
                      {new Date(run.startTime).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  {run.summary && (
                    <div className="history-summary">
                      <span>{run.summary.total} scénarios</span>
                      <span className="pass-rate">{run.summary.passRate}% réussite</span>
                      <span>{run.summary.passed} ✅ / {run.summary.failed} ❌</span>
                    </div>
                  )}
                  {run.error && <p className="history-error">❌ {run.error}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="exploratory-footer">
        <span className="footer-brand">
          <strong>FARA-7B</strong> · 7B params · MIT License · Microsoft Research
        </span>
        <span className="footer-info">
          Computer Use Agent · Observe → Think → Act
        </span>
      </div>
    </div>
  );
};

function formatDuration(ms) {
  if (!ms) return '0s';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

export default ExploratoryTestRunner;
